"use client";

import { NavUser } from "@/shared/components/ui/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarCustomInput,
} from "@/shared/components/ui/sidebar";
import { NavSecondary } from "@/shared/components/ui/nav-secondary";
import {
  navMainItems,
  settingsNavItems,
} from "@/shared/constants/navbar-list-items/layouts-list-items";
import LogoSymbol from "@/shared/components/logo/logo-symbol";
import { getDashboardLink } from "@/shared/utils/link";
import { useEffect, useRef, useState, useCallback } from "react";
import {
  Plus,
  Search,
  ArrowDown,
  Ellipsis,
  ListCollapse,
  ArrowDownUp,
  Check,
} from "lucide-react";
import { KeyboardIcon } from "@/shared/constants/images/icon-files";
import { SpecificationCategoryList } from "../../../pages/create/category-sidebar/category-sidebar-content/category-list/category-list";
import { Input } from "@/shared/components/ui/input";
import {
  setActiveEmptySpecification,
  setActiveSpecificationItem,
  setIsInputFocused,
  setLoading,
  setOpenCategoriesCollapse,
  setSearchTerm,
  setIsSpecificationCreateSidebarOpen,
  setDashboardSidebarWidth,
} from "@/core/redux/slices/specification";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDispatch, useSelector } from "@/core/redux/store";
import { Typography } from "@/shared/components/ui/typography";
import { DndProvider } from "react-dnd";
import { useDrop } from "react-dnd";
import { specificationService } from "@/shared/services/api/specification-service";
import {
  setNameSpecification,
  setSpecificationDetail,
} from "@/core/redux/slices/specification";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { Card } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { ISpecificationCategoryCreateRequest } from "@/shared/services/interface/requests/specification";
import { getCategoryAndItemThunk } from "@/core/redux/thunks/specificationThunk";
import { debounce } from "lodash";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { supabaseResponseToItem } from "@/core/redux/helper/specification";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { DashboardSidebar } from "@/shared/layouts/dashboard-layout/dashboard-sidebar";
import { trackSpecificationSaved } from "@/lib/rum-actions";

export enum SortType {
  ASC = "ASC",
  DESC = "DESC",
  LAST_UPDATED = "lastUpdated",
  FIRST_UPDATED = "firstUpdated",
  IMPORTANCE_SCORE_HIGHEST = "importanceScoreHighest",
  IMPORTANCE_SCORE_LOWEST = "importanceScoreLowest",
  ADDED_BY = "addedBy",
}

export function SpecificationSidebar({
  disableEdit = false,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  disableEdit?: boolean;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const specificationState = useSelector((state) => state.specification);
  const user = useSelector((state) => state.user.me);
  const categories = useSelector(
    (state) => state.specification.filteredCategories
  );
  const searchTerm = useSelector((state) => state.specification.searchTerm);
  const { items, subCategories, mainCategories } = useSelector(
    (state) => state.specification.changedValues
  );

  const specification = specificationState;
  const allCategories = specification.allCategoryNames;
  const me = user;

  const [collapse, setCollapse] = useState(true);
  const [sort, setSort] = useState<SortType>(SortType.LAST_UPDATED);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const setName = (name: string) => {
    dispatch(setNameSpecification(name));
  };

  const updateSpecification = useCallback(
    async (name: string) => {
      console.log('[RUM-008 DEBUG] updateSpecification called with name:', name);
      console.log('[RUM-008 DEBUG] user.company.id:', user?.company.id);
      console.log('[RUM-008 DEBUG] specification.id:', specificationState.specification.specification.id);
      if (
        !user?.company.id ||
        !specificationState.specification.specification.id
      ) {
        console.warn('[RUM-008 DEBUG] Early return - IDs missing');
        return;
      }
      console.log('[RUM-008 DEBUG] Making API call...');
      const startTime = Date.now();
      const response = await specificationService.updateSpecification({
        companyId: user?.company.id,
        specificationId: specificationState.specification.specification.id,
        name: name,
      });

      if (response.success) {
        // RUM-008: Track specification saved (auto-save via name change)
        const saveDurationMs = Date.now() - startTime;
        trackSpecificationSaved(
          specificationState.specification.specification.id,
          items.length,
          'auto',
          saveDurationMs,
          true
        );
        dispatch(setSpecificationDetail(response.data));
      }

      return response;
    },
    [
      user?.company.id,
      specificationState.specification.specification.id,
      dispatch,
    ]
  );

  const debouncedUpdateSpecification = useMemo(
    () => {
      console.log('[RUM-008 DEBUG] Creating NEW debounced function');
      return debounce(async (name: string) => {
        console.log('[RUM-008 DEBUG] Debounce fired! Calling updateSpecification...');
        await updateSpecification(name);
      }, 1000);
    },
    [
      updateSpecification,
      user?.company.id,
      specificationState.specification.specification.id,
    ]
  );

  const handleChangeName = useCallback(
    (name: string) => {
      console.log('[RUM-008 DEBUG] handleChangeName called with:', name);
      if (
        !user?.company.id ||
        !specificationState.specification.specification.id
      ) {
        console.warn("Gerekli ID'ler eksik");
        return;
      }
      console.log('[RUM-008 DEBUG] Scheduling debounced update...');
      debouncedUpdateSpecification(name);
    },
    [
      debouncedUpdateSpecification,
      user?.company.id,
      specificationState.specification.specification.id,
    ]
  );

  useEffect(() => {
    return () => {
      console.log('[RUM-008 DEBUG] Cleanup: Cancelling debounced function');
      debouncedUpdateSpecification.cancel();
    };
  }, [debouncedUpdateSpecification]);
  const unreadInboxCount = useSelector((state) => state.user.unreadInboxCount);
  const [openSpecificationListOptions, setOpenSpecificationListOptions] =
    useState(false);
  const [isHoveringSpecificationSidebar, setIsHoveringSpecificationSidebar] =
    useState(false);
  const isSpecificationCreateSidebarOpen = useSelector(
    (state) => state.specification.isSpecificationCreateSidebarOpen
  );
  const dashboardSidebarWidth = useSelector(
    (state) => state.specification.dashboardSidebarWidth
  );
  const specificationSidebarWidth = useSelector(
    (state) => state.specificationList.sidebarWidth
  );

  const [data, setData] = useState({
    user: {
      name: t("email"),
      email: me?.email || "",
      avatar: "/avatars/shadcn.jpg",
    },
    navMain: navMainItems(t),
    navSecondary: settingsNavItems(t, true),
  });

  const [firstLoad, setFirstLoad] = useState(true);
  const activeItem = data.navMain[0];

  const searchInputRef = useRef<HTMLInputElement>(null);
  const sidebarContentRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    setData({
      ...data,
      navSecondary: settingsNavItems(t, true),
    });
  }, [unreadInboxCount]);

  useEffect(() => {
    if (firstLoad && items.length > 0) {
      const item = items[0];
      const firstItem = supabaseResponseToItem(item);
      dispatch(setActiveSpecificationItem(firstItem));
      setFirstLoad(false);
    }
  }, [items, firstLoad]);

  // Scroll durumunu kontrol et
  useEffect(() => {
    const scrollContainer = sidebarContentRef.current;
    if (!scrollContainer) return;

    const checkScrollState = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const hasScrollableContent = scrollHeight > clientHeight;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px tolerance

      setShowScrollToBottom(hasScrollableContent && !atBottom);
    };

    checkScrollState();
    scrollContainer.addEventListener("scroll", checkScrollState);
    window.addEventListener("resize", checkScrollState);

    return () => {
      scrollContainer.removeEventListener("scroll", checkScrollState);
      window.removeEventListener("resize", checkScrollState);
    };
  }, [categories]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Dashboard sidebar genişliği specification sidebar genişliğinden küçükse güncelle
  useEffect(() => {
    const minWidth = Math.max(320, specificationSidebarWidth);
    if (dashboardSidebarWidth < minWidth) {
      dispatch(setDashboardSidebarWidth(minWidth));
    }
  }, [specificationSidebarWidth, dashboardSidebarWidth, dispatch]);

  const MainCategoryDropZone = ({
    children,
  }: {
    children: React.ReactNode;
  }) => {
    const user = useSelector((state) => state.user.me);
    const specification = useSelector((state) => state.specification);

    const [{ isOver }, dropRef] = useDrop({
      accept: "CATEGORY",
      drop: async (
        draggedItem: { id: string; type: string; name: string },
        monitor
      ) => {
        if (monitor.didDrop()) {
          return;
        }

        if (draggedItem.type === "CATEGORY" && user?.company.id) {
          await specificationService.updateSpecificationCategory({
            categoryId: draggedItem.id,
            name: draggedItem.name,
            parentId: null,
            companyId: user.company.id,
            specificationId: specification.specification.specification.id,
          });
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
      }),
    });

    return (
      <div
        ref={dropRef}
        className={`rounded-md transition-colors`}
        style={{
          backgroundColor: isOver ? "rgba(0,0,0,0.1)" : "transparent",
        }}
      >
        {children}
      </div>
    );
  };

  const addNewCategory = async (parentId?: string) => {
    const existingCategories = allCategories.filter((name: string) =>
      name.toLowerCase().includes(t("new-category").toLowerCase())
    );

    if (specification.specification.specification.id) {
      let sendData = {
        name:
          existingCategories.length > 0
            ? `${t("new-category")} ${existingCategories.length + 1}`
            : t("new-category"),
        specificationId: specification.specification.specification.id,
      } as ISpecificationCategoryCreateRequest;

      sendData.parentId = parentId || null;

      const response = await specificationService.createSpecificationCategory(
        sendData
      );

      if (response.success) {
        // Kategori eklendiğinde state'i güncelle
        if (me?.company.id) {
          dispatch(
            getCategoryAndItemThunk({
              specificationId: specification.specification.specification.id,
              companyId: me.company.id,
            })
          );
        }

        dispatch(
          setActiveEmptySpecification({
            categoryId: response.data.id,
            isNew: true,
          })
        );
        // scroll to the bottom of the sidebar
        setTimeout(() => {
          if (sidebarContentRef.current) {
            sidebarContentRef.current.scrollTo({
              top: sidebarContentRef.current.scrollHeight,
              behavior: "smooth",
            });
          }
        }, 300);
      }
    }
  };

  const debouncedSearch = useCallback(
    debounce(async (search: string) => {
      if (!me?.company.id) return;
      const response = await dispatch(
        getCategoryAndItemThunk({
          specificationId: specification.specification.specification.id,
          companyId: me?.company.id,
          search: search || "",
          userId: me?.id || undefined,
        })
      );

      dispatch(setSearchTerm(search));
      if (response) {
        setTimeout(() => {
          dispatch(setLoading(false));
        }, 1000);
      }
    }, 800),
    [me?.company.id, specification.specification.specification.id]
  );

  const scrollToBottom = () => {
    const scrollContainer = sidebarContentRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  return (
    <Sidebar
      collapsible="icon"
      className="overflow-hidden [&>[data-sidebar=sidebar]]:flex-row scrollbar-hide"
      onMouseEnter={() => setIsHoveringSpecificationSidebar(true)}
      onMouseLeave={() => setIsHoveringSpecificationSidebar(false)}
      {...props}
    >
      {/* Icon Sidebar Navigation */}
      <div
        className={`fixed inset-y-0 left-0 z-30 transition-all duration-300 ease-in-out ${
          isSpecificationCreateSidebarOpen
            ? "translate-x-0"
            : "-translate-x-full"
        }`}
        style={{
          willChange: "transform",
          width: `${dashboardSidebarWidth}px`,
        }}
        onMouseLeave={() => {
          if (
            isSpecificationCreateSidebarOpen &&
            !isHoveringSpecificationSidebar
          ) {
            if (closeTimeoutRef.current) {
              clearTimeout(closeTimeoutRef.current);
            }
            closeTimeoutRef.current = setTimeout(() => {
              dispatch(setIsSpecificationCreateSidebarOpen(false));
              closeTimeoutRef.current = null;
            }, 1000);
          }
        }}
        onMouseEnter={() => {
          // Mouse geri döndüğünde timer'ı iptal et
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
          }
        }}
      >
        <div
          className="relative h-full w-full"
          style={
            {
              width: `${dashboardSidebarWidth}px`,
              "--sidebar-width": `${dashboardSidebarWidth}px`,
            } as React.CSSProperties
          }
        >
          <DashboardSidebar />
        </div>
      </div>
      {/* Kapalı durumda görünen border/handle */}
      {!isSpecificationCreateSidebarOpen && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="fixed left-0 top-0 bottom-0 z-[29] w-1 bg-zinc-300 hover:bg-zinc-400 hover:w-2 transition-all duration-200 cursor-ew-resize"
                onClick={() =>
                  dispatch(setIsSpecificationCreateSidebarOpen(true))
                }
              />
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={8}
              className="bg-[#09090B] text-white border-none rounded-[8px] py-1.5 px-3"
            >
              <span className="text-[13px] font-500">{t("open-sidebar")}</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <Sidebar
        collapsible="none"
        className="!w-[calc(var(--sidebar-width-icon)_+_1px)] group-data-[side=left]:border-r relative  "
      >
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarGroup className="p-2">
                <a href={getDashboardLink("")}>
                  <LogoSymbol />
                </a>
              </SidebarGroup>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent className="px-1.5 md:px-0">
              <SidebarMenu className="side-icon-menu">
                {data.navMain.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      tooltip={{
                        children: item.title,
                        hidden: false,
                      }}
                      onClick={() => {
                        navigate(item.url);
                      }}
                      isActive={activeItem.title === item.title}
                      className="px-2.5 md:px-2 "
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <NavSecondary
            items={data.navSecondary}
            className="mt-auto mb-[40px]"
          />
        </SidebarContent>
        <SidebarFooter>
          <NavUser user={data.user} absoluteProfile />
        </SidebarFooter>
      </Sidebar>
      <Sidebar collapsible="none" className="hidden flex-1 md:flex relative">
        <SidebarHeader className="gap-2 w-full">
          <div className="flex w-full items-center justify-between">
            <Input
              disabled={disableEdit}
              className="w-full sidebar-input-title text-zinc-950 font-600 h-[32px] bg-transparent border-none
              focus:bg-white focus:rounded-md focus:border focus:border-input focus:ring-0
              px-3 focus:py-0 focus:shadow-none focus:font-600 md:focus:text-sm"
              value={specification.specification.specification.name}
              placeholder={t("namelessSpecification")}
              onFocus={(e) => {
                dispatch(setIsInputFocused(true));
                e.target.select();
              }}
              onBlur={() => dispatch(setIsInputFocused(false))}
              onChange={(e) => {
                setName(e.target.value);
                handleChangeName(e.target.value);
              }}
            />
          </div>
          <SidebarCustomInput
            disabled={disableEdit}
            ref={searchInputRef}
            leftElement={
              !firstLoad && specification.specificationLoading ? (
                <DotLottieReact
                  src="/animations/lottie/loading-spinner.json"
                  style={{
                    width: "35px",
                    height: "35px",
                    marginLeft: "-10px",
                  }}
                  autoplay={true}
                  loop={true}
                />
              ) : (
                <Search width={16} height={16} className="-mt-[1px]" />
              )
            }
            rightElement={
              <img src={KeyboardIcon} alt="keyboard" width={28} height={20} />
            }
            onFocus={() => {
              dispatch(setIsInputFocused(true));
              setIsSearchFocused(true);
            }}
            onBlur={() => {
              dispatch(setIsInputFocused(false));
              setIsSearchFocused(false);
            }}
            onChange={(e) => {
              dispatch(setLoading(true));
              debouncedSearch(e.target.value);
            }}
            placeholder={isSearchFocused ? "" : t("search")}
            className="h-[32px] font-600"
          />
        </SidebarHeader>
        <SidebarContent ref={sidebarContentRef} className="relative">
          <SidebarGroup>
            <SidebarGroupContent>
              <DndProvider backend={HTML5Backend}>
                <MainCategoryDropZone>
                  {!specification.skeletonLoading && (
                    <SidebarMenuButton
                      asChild
                      className="mb-0 py-1 px-2 min-h-[32px]"
                    >
                      <Typography
                        variant="bodyMuted"
                        className="categories-title font-600 flex items-center justify-between "
                      >
                        {t("categories")}
                        {!disableEdit && (
                          <div className="flex items-center gap-2">
                            <Tooltip>
                              <TooltipTrigger>
                                <DropdownMenu
                                  open={openSpecificationListOptions}
                                  onOpenChange={setOpenSpecificationListOptions}
                                >
                                  <DropdownMenuTrigger asChild>
                                    <Card
                                      className={`w-[18px] cursor-pointer border-none ring-0 shadow-none hover:bg-secondary bg-transparent  h-[18px] flex items-center justify-center py-0.5 px-0.5 rounded-sm  focus:bg-secondary hover:bg-[#e5e4e7] `}
                                      onClick={() =>
                                        setOpenSpecificationListOptions(true)
                                      }
                                    >
                                      <Ellipsis width={16} height={16} />
                                    </Card>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="w-[190px]">
                                    <DropdownMenuItem
                                      className="cursor-pointer"
                                      onClick={() => {
                                        dispatch(
                                          setOpenCategoriesCollapse(!collapse)
                                        );
                                        setCollapse(!collapse);
                                      }}
                                    >
                                      <ListCollapse width={16} height={16} />
                                      <span className="text-[14px] font-500 text-zinc-950 mt-0.5">
                                        {t("collapse")}
                                      </span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSub>
                                      <DropdownMenuSubTrigger className="cursor-pointer">
                                        <ArrowDownUp width={16} height={16} />
                                        <span className="text-[14px] font-500 text-zinc-950 mt-0.5">
                                          {t("sort")}
                                        </span>
                                      </DropdownMenuSubTrigger>
                                      <DropdownMenuSubContent className="w-fit pr-1">
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() =>
                                            setSort(SortType.LAST_UPDATED)
                                          }
                                        >
                                          <Check
                                            width={16}
                                            height={16}
                                            className={
                                              sort === SortType.LAST_UPDATED
                                                ? "opacity-100"
                                                : "opacity-0"
                                            }
                                          />
                                          <span className="text-[14px] font-500 text-zinc-950 mt-0.5">
                                            {t("last-updated")}
                                          </span>
                                        </DropdownMenuItem>

                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() =>
                                            setSort(SortType.FIRST_UPDATED)
                                          }
                                        >
                                          <Check
                                            width={16}
                                            height={16}
                                            className={
                                              sort === SortType.FIRST_UPDATED
                                                ? "opacity-100"
                                                : "opacity-0"
                                            }
                                          />
                                          <span className="text-[14px] font-500 text-zinc-950 mt-0.5">
                                            {t("first-updated")}
                                          </span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() =>
                                            setSort(
                                              SortType.IMPORTANCE_SCORE_HIGHEST
                                            )
                                          }
                                        >
                                          <Check
                                            width={16}
                                            height={16}
                                            className={
                                              sort ===
                                              SortType.IMPORTANCE_SCORE_HIGHEST
                                                ? "opacity-100"
                                                : "opacity-0"
                                            }
                                          />
                                          <span className="text-[14px] font-500 text-zinc-950 mt-0.5">
                                            {t("importance-score-highest")}
                                          </span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() =>
                                            setSort(
                                              SortType.IMPORTANCE_SCORE_LOWEST
                                            )
                                          }
                                        >
                                          <Check
                                            width={16}
                                            height={16}
                                            className={
                                              sort ===
                                              SortType.IMPORTANCE_SCORE_LOWEST
                                                ? "opacity-100"
                                                : "opacity-0"
                                            }
                                          />
                                          <span className="text-[14px] font-500 text-zinc-950 mt-0.5">
                                            {t("importance-score-lowest")}
                                          </span>
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator className="bg-zinc-200" />
                                        <DropdownMenuSub>
                                          <DropdownMenuSubTrigger>
                                            <Check
                                              width={16}
                                              height={16}
                                              className={
                                                sort === SortType.ADDED_BY
                                                  ? "opacity-100"
                                                  : "opacity-0"
                                              }
                                            />
                                            <span className="text-[14px] font-500 text-zinc-950 mt-0.5">
                                              {t("added-by")}
                                            </span>
                                          </DropdownMenuSubTrigger>
                                          <DropdownMenuSubContent></DropdownMenuSubContent>
                                        </DropdownMenuSub>
                                        <DropdownMenuSeparator className="bg-zinc-200" />
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() => setSort(SortType.ASC)}
                                        >
                                          <Check
                                            width={16}
                                            height={16}
                                            className={
                                              sort === SortType.ASC
                                                ? "opacity-100"
                                                : "opacity-0"
                                            }
                                          />
                                          <span className="text-[14px] font-500 text-zinc-950 mt-0.5">
                                            {t("asc")}
                                          </span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() => setSort(SortType.DESC)}
                                        >
                                          <Check
                                            width={16}
                                            height={16}
                                            className={
                                              sort === SortType.DESC
                                                ? "opacity-100"
                                                : "opacity-0"
                                            }
                                          />
                                          <span className="text-[14px] font-500 text-zinc-950 mt-0.5">
                                            {t("desc")}
                                          </span>
                                        </DropdownMenuItem>
                                      </DropdownMenuSubContent>
                                    </DropdownMenuSub>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TooltipTrigger>
                              <TooltipContent
                                className="z-[9999999999]"
                                side="left"
                              >
                                <span className="text-[13px] font-500 text-zinc-50">
                                  {t("specification-list-options")}
                                </span>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger>
                                <Card
                                  className={`w-[18px] cursor-pointer border-none ring-0 shadow-none hover:bg-secondary bg-transparent  h-[18px] flex items-center justify-center py-0.5 px-0.5 rounded-sm  focus:bg-secondary hover:bg-[#e5e4e7] `}
                                  onClick={() => addNewCategory()}
                                >
                                  <Plus width={16} height={16} />
                                </Card>
                              </TooltipTrigger>
                              <TooltipContent
                                className="z-[9999999999]"
                                side="left"
                              >
                                <span className="text-[13px] font-500 text-zinc-50">
                                  {t("add-category")}
                                </span>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        )}
                      </Typography>
                    </SidebarMenuButton>
                  )}
                  <SpecificationCategoryList
                    categories={categories}
                    disableEdit={disableEdit}
                  />
                </MainCategoryDropZone>
              </DndProvider>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Scroll to Bottom Button */}
        </SidebarContent>
        {showScrollToBottom && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[9999999999]">
            <Tooltip>
              <TooltipTrigger>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-[28px] w-[28px] p-0 rounded-full hover:bg-zinc-200 backdrop-blur-sm transition-all duration-200 "
                  onClick={scrollToBottom}
                  title="Aşağı kaydır"
                >
                  <ArrowDown className="h-[7px] w-[7px] text-zinc-700" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <span className="text-[13px] font-500 text-zinc-50">
                  {t("scrollToBottomList")}
                </span>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
        <SidebarFooter></SidebarFooter>
      </Sidebar>
    </Sidebar>
  );
}
